import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../routes';

const SEVERITIES = { Mild:'badge-success', Moderate:'badge-warning', Severe:'badge-danger', 'Life-threatening':'badge-danger' };

export default function Dashboard({ patients, onDelete, onEdit, currentUser }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const filtered = patients.filter(p => {
    return [p.name, p.regNo, p.diagnosis, p.phone].join(' ').toLowerCase().includes(search.toLowerCase());
  });

  const stats = [
    { icon:'👥', label:'Total Reports', value: patients.length, cls:'blue' },
  ];

  return (
    <>
      <div className="stats-row">
        {stats.map(s => (
          <div className={`stat-card ${s.cls}`} key={s.label}>
            <div className="stat-icon">{s.icon}</div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="records-section">
        <div className="records-header">
          <div>
            <div className="section-title">Patient Records</div>
            <div className="section-subtitle">{filtered.length} of {patients.length} ADR report(s)</div>
          </div>
          <div className="records-controls">
            <div className="search-box">
              <span className="search-icon">🔍</span>
              <input placeholder="Search name, reg no..." value={search} onChange={e=>setSearch(e.target.value)} />
            </div>
            <button className="btn btn-primary" style={{padding:'8px 18px',fontSize:'0.82rem'}} onClick={() => navigate(ROUTES.REGISTER_PERSONAL)}>
              + New Report
            </button>
          </div>
        </div>

        <div className="records-grid">
          {filtered.length === 0 && (
            <div className="empty-state">
              <div className="empty-state-icon">📭</div>
              <div className="empty-state-title">{patients.length === 0 ? 'No reports yet' : 'No results found'}</div>
              <div className="empty-state-desc">{patients.length === 0 ? 'Submit your first ADR report to see it here.' : 'Try a different search or filter.'}</div>
              {patients.length === 0 && <button className="btn btn-primary" style={{marginTop:'20px'}} onClick={() => navigate(ROUTES.REGISTER_PERSONAL)}>Submit First Report</button>}
            </div>
          )}
          {filtered.map(p => (
            <div className="record-card" key={p.id} onClick={() => setSelected(p)}>
              <div className="record-card-header">
                <div className="record-patient-info">
                  <div className="patient-avatar">{p.name?.[0]||'?'}</div>
                  <div>
                    <div className="patient-name">{p.name}</div>
                    <div className="patient-reg">📋 {p.regNo || 'N/A'}</div>
                  </div>
                </div>
                {p.severity && <span className={`badge ${SEVERITIES[p.severity]||'badge-primary'}`}>{p.severity}</span>}
              </div>

              <div className="record-meta">
                <div className="record-meta-item"><span className="meta-icon">📞</span>{p.phone||'N/A'}</div>
                <div className="record-meta-item"><span className="meta-icon">🩸</span>{p.bloodGroup||'N/A'}</div>
                <div className="record-meta-item"><span className="meta-icon">👤</span>{p.age ? `${p.age} yrs` : 'N/A'} • {p.gender||'N/A'}</div>
              </div>

              {p.diagnosis && (
                <div className="record-diagnosis">
                  <div className="record-diagnosis-label">💊 Diagnosis</div>
                  <div className="record-diagnosis-text">{p.diagnosis}</div>
                </div>
              )}

              <div className="record-diagnosis" style={{ marginTop: '10px', background: 'rgba(239, 68, 68, 0.05)' }}>
                <div className="record-diagnosis-label" style={{ color: '#dc2626' }}>⚠️ Side Effects Reported</div>
                <div className="record-diagnosis-text">
                  {p.sideEffects && p.sideEffects.length > 0 
                    ? p.sideEffects.join(', ')
                    : <span style={{color: 'var(--text-muted)'}}>No side effects reported</span>}
                </div>
              </div>

              <div className="record-footer">
                <span className="record-date">🕐 {new Date(p.createdAt).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</span>
                <div className="record-actions">
                  <button type="button" className="action-btn" title="View" onClick={e=>{e.stopPropagation();setSelected(p);}}>👁 View</button>
                  <button type="button" className="action-btn" title="Edit" onClick={e=>{e.stopPropagation();onEdit(p);}}>✏️ Edit</button>
                  {currentUser?.role === 'superadmin' && (
                    <button type="button" className="action-btn delete" title="Delete" onClick={e=>{e.stopPropagation();onDelete(p.id);}}>🗑 Delete</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="patient-name" style={{fontSize:'1.3rem',marginBottom:'4px'}}>{selected.name}</div>
                <div className="patient-reg">Reg: {selected.regNo} &nbsp;|&nbsp; Submitted: {new Date(selected.createdAt).toLocaleString('en-IN')}</div>
              </div>
              <button className="modal-close" onClick={() => setSelected(null)}>✕</button>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',marginBottom:'20px'}}>
              {[['Phone',selected.phone],['Age',selected.age ? selected.age+' yrs' : 'N/A'],['Gender',selected.gender||'N/A'],['Blood',selected.bloodGroup||'N/A'],['Severity',selected.severity||'N/A'],['Report Date',selected.reportDate ? new Date(selected.reportDate).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : 'N/A'],['Date of Reaction',selected.reactionDate ? new Date(selected.reactionDate).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : 'N/A']].map(([k,v])=>(
                <div key={k} style={{background:'var(--bg-input)',borderRadius:'8px',padding:'10px 14px'}}>
                  <div style={{fontSize:'0.7rem',color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'4px'}}>{k}</div>
                  <div style={{fontSize:'0.9rem',color:'var(--text-primary)',fontWeight:600}}>{v}</div>
                </div>
              ))}
            </div>

            {selected.address && <div style={{marginBottom:'12px'}}><span style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>📍 Address: </span><span style={{fontSize:'0.875rem',color:'var(--text-secondary)'}}>{selected.address}</span></div>}

            <div className="record-diagnosis" style={{marginBottom:'12px'}}>
              <div className="record-diagnosis-label">Diagnosis</div>
              <div className="record-diagnosis-text">{selected.diagnosis}</div>
            </div>

            {selected.drugRegimen && (
              <div className="record-diagnosis" style={{marginBottom:'12px'}}>
                <div className="record-diagnosis-label">💊 Drug Regimen</div>
                <div className="record-diagnosis-text">{selected.drugRegimen}</div>
              </div>
            )}

            {selected.sideEffects && Array.isArray(selected.sideEffects) && selected.sideEffects.length > 0 && (
              <div className="record-diagnosis" style={{marginBottom:'12px'}}>
                <div className="record-diagnosis-label">⚠️ Side Effects Reported</div>
                <div style={{fontSize:'0.875rem',color:'var(--text-secondary)',lineHeight:1.7,marginTop:'6px'}}>
                  {selected.sideEffects.map((effect, idx) => {
                    const cycleDetails = selected.sideEffectCycles?.[effect];
                    const cycleText = cycleDetails?.cycle === 'Other'
                      ? cycleDetails.other
                      : cycleDetails?.cycle;
                    const freqText = cycleDetails?.frequency
                      ? ` (${cycleDetails.frequency} times)`
                      : '';
                    const remedyText = cycleDetails?.remedy === 'Other (અન્ય)'
                      ? cycleDetails.remedyOther
                      : cycleDetails?.remedy;
                    const remedyDisplay = remedyText ? ` | Remedy: ${remedyText}` : '';
                    const reducedText = cycleDetails?.reduced ? ` | Reduced: ${cycleDetails.reduced}` : '';

                    return (
                      <div key={idx} style={{display:'inline-block',background:'rgba(239,68,68,0.1)',color:'#dc2626',padding:'4px 10px',borderRadius:'4px',margin:'4px 4px 4px 0',fontSize:'0.8rem',fontWeight:500}}>
                        {effect}{cycleText ? ` - Cycle ${cycleText}` : ''}{freqText}{remedyDisplay}{reducedText}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {selected.medications && (
              <div className="record-diagnosis" style={{marginBottom:'12px'}}>
                <div className="record-diagnosis-label">💊 Medications</div>
                <div className="record-diagnosis-text">{selected.medications}</div>
              </div>
            )}

            {selected.allergies && (
              <div style={{marginBottom:'12px'}}><span style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>⚠ Allergies: </span><span style={{fontSize:'0.875rem',color:'#fbbf24'}}>{selected.allergies}</span></div>
            )}


          </div>
        </div>
      )}
    </>
  );
}
